import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import { TableRow, TableCell, Avatar, IconButton } from '@material-ui/core';
import { colors, fontSize } from '../../../constants/styleConstants';
import { useFBTableStyles } from '../../../components/FbTable';
import FbTableCard, { useTableCardStyles } from '../../../components/FbTable/FbTableCard';
import route, { actionsPagesRoutes } from '../../../constants/routes';
import EditFbIcon from '../../../components/Icons/EditIcon';
import TextButton from '../../../components/TextButton';
import NextLink from '../../../components/NextLink';
import { getDynamicRoutePropsForNewTab, getRouteWithSlug } from '../../../utils/common';
import { AGREEMENTS_TYPES } from '../constants';
import { TP } from '../../../constants/consts';
import DeleteFbIcon from '../../../components/Icons/DeleteFbIcon';
import Button from '../../../components/Button';

const useStyles = makeStyles(theme => ({
  agreementsInfo: {
    textTransform: 'capitalize',
  },
  regionItem: {
    padding: '0px 5px',
    color: '#565656',
    fontSize: fontSize.smallTitle,
    borderRadius: 1.4,
    backgroundColor: `${colors.background.default} !important`,
    fontWeight: 400,
    marginRight: 5,
    display: 'inline-block',
    wordBreak: 'break-word',
  },
  innerCell: {
    borderLeft: `1px solid ${colors.linesGray}`,
  },
  avatar: {
    marginRight: 10,
  },
  artistNameBlock: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    padding: `0 ${theme.spacing(1)}px`,
  },
  editButtonsBlock: {
    marginLeft: 'auto',
    maxWidth: 80,
    padding: 10,
    display: 'flex',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '80%',
      flexDirection: 'column',
    },
  },
  editButton: {
    marginRight: 10,
    marginLeft: 10,
  },
  bold: {
    fontWeight: 'bold',
  },
  label: {
    color: colors.subTitle,
    fontWeight: 'bold',
  },
  requestLink: {
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      marginBottom: theme.spacing(2),
    },
  },
  agencyLink: {
    display: 'block',
  },
  greyChip: {
    textTransform: 'uppercase',
    backgroundColor: colors.background.default,
  },
  agreementType: {
    color: colors.cardTitle,
    fontSize: fontSize.smallTitle,
    fontWeight: 'bold',
    textTransform: 'uppercase',
  },
  generalManager: {
    fontWeight: 'bold',
    fontSize: fontSize.medium,
  },
  representative: {
    fontSize: fontSize.medium,
  },
  representativeName: {
    fontWeight: 'bold',
    fontSize: fontSize.medium,
  },
  exclusiveTerritories: {
    width: '60%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
}));

export const AgreementsData = React.memo(({ agreements, t, commonClasses }) => {
  const classes = useStyles();
  const generalManagementIndex = agreements.findIndex(
    agr => get(agr, 'agreementType') === AGREEMENTS_TYPES.GENERAL_MENEGMENT,
  );
  const isGeneralManagement = generalManagementIndex !== -1;
  let noGeneralManagementAgreements = agreements;
  if (isGeneralManagement) {
    noGeneralManagementAgreements = agreements.filter(
      agr => get(agr, 'agreementType', '') !== AGREEMENTS_TYPES.GENERAL_MENEGMENT,
    );
  }
  const contactPersons = get(agreements, '0.contactPersons', []);

  return (
    <>
      {isGeneralManagement && (
        <span className={commonClasses.generalManager}>
          {t(`${TP}.m_EDIT_ROSTER_LIST_GENERALMANAGER`)}
          <span className={commonClasses.generalManager}> {t(`${TP}.m_LOCALL`)}</span>
        </span>
      )}
      {noGeneralManagementAgreements.map(agreement => (
        <div key={get(agreement, 'id')}>
          <span className={commonClasses.agreementType}>{get(agreement, 'agreementType', '')}</span>{' '}
          {get(agreement, 'exclusive') ? (<span className={commonClasses.agreementType}>{t(`${TP}.m_TERRITORIES_E`)}</span>) : (<span className={commonClasses.agreementType}>{t(`${TP}.m_TERRITORIES_NE`)}</span>)}
          {': '}
          {get(agreement, 'includedCountries', []).map(region => (
            <span key={`includedCountries-${get(region, 'id')}`} className={classes.regionItem}>
              {get(region, 'name', '')}
            </span>
          ))}
          {get(agreement, 'exclusive') && get(agreement, 'excludedCountries', []).length > 0 && (
            <>
              {`${t(`${TP}.REPRESENTATION_EXCLUDING`)}: `}
              {get(agreement, 'excludedCountries', []).map(region => (
                <span key={`excludedCountries-${get(region, 'id')}`} className={classes.regionItem}>
                  {get(region, 'name', '')}
                </span>
              ))}
            </>
          )}
          {contactPersons.length > 0 && (
            <div>
              <span className={commonClasses.representative}>{t(`${TP}.FN_AGENCY_REPRESENTATIVE`)}: </span>
              {contactPersons.map((el, index) => (
                <span className={commonClasses.representativeName}>
                  {el.agentName}
                  {contactPersons[index + 1] ? ', ' : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </>
  );
});

export const AgreementsDataV1 = React.memo(({ agreements, t, commonClasses, isMinimized }) => {
  const classes = useStyles();
  const generalManagementIndex = agreements.findIndex(
    agr => get(agr, 'agreementType') === AGREEMENTS_TYPES.GENERAL_MENEGMENT,
  );
  const isGeneralManagement = generalManagementIndex !== -1;
  let noGeneralManagementAgreements = agreements;
  if (isGeneralManagement) {
    noGeneralManagementAgreements = agreements.filter(
      agr => get(agr, 'agreementType', '') !== AGREEMENTS_TYPES.GENERAL_MENEGMENT,
    );
  }

  return (
    <>
      {isGeneralManagement && (
        <span className={commonClasses.bold}>{`${t(`${TP}.FN_GENERAL`)} ${t(`${TP}.m_LOCALL`)}`}</span>
      )}
      {noGeneralManagementAgreements.map(agreement => (
        <div className={commonClasses.agreementsInfo} key={get(agreement, 'id')}>
          <div className={classes.exclusiveTerritories}>
            <span className={commonClasses.label}>
              {`"${get(agreement, 'agreementType', '')}"`}{' '}
              {get(agreement, 'exclusive') ? t(`${TP}.m_TERRITORIES_E`) : t(`${TP}.m_TERRITORIES_NE`)}
            </span>
          </div>
          <div className={classes.countries}>
            {get(agreement, 'includedCountries', []).map(region => (
              <span className={classes.regionItem} key={`includedCountries-${get(region, 'id')}`}>
                {get(region, 'name', '')}
              </span>
            ))}
          </div>
          {get(agreement, 'exclusive') && get(agreement, 'excludedCountries', []).length > 0 && (
            <div>
              <Grid item xs={12} md={isMinimized ? 12 : 6}>
                <span className={classes.label}> {`${t(`${TP}.REPRESENTATION_EXCLUDING`)} `}</span>
              </Grid>
              <Grid item xs={12} md={isMinimized ? 12 : 6}>
                {get(agreement, 'excludedCountries', []).map(region => (
                  <span className={classes.regionItem} key={`excludedCountries-${get(region, 'id')}`}>
                    {get(region, 'name', '')}
                  </span>
                ))}
              </Grid>
            </div>
          )}
        </div>
      ))}
    </>
  );
});

const addAgreementPageUrl = (agencyUrl, artistData) => ({
  href: `${agencyUrl}/${actionsPagesRoutes.ADD_AGREEMENTS}?artist_id=${get(artistData, 'id')}`,
});

const ArtistInfo = React.memo(({ data, classes, t }) => (
  <>
    <Avatar
      src={get(data, 'artist.profile.image.small')}
      alt={get(data, 'artist.profile.name') || t(`${TP}.m_NO_VALUE}`)}
      className={classes.avatar}
    />
    {get(data, 'artist.profile.id') ? (
      <NextLink
        target="_blank"
        href={`${route.ARTISTS}/[id]`}
        as={getRouteWithSlug(route.ARTISTS, get(data, 'artist.profile'))}
      >
        {get(data, 'artist.profile.name', '')}
      </NextLink>
    ) : (
      get(data, 'artist.profile.name', '')
    )}
  </>
));

const EditButtons = React.memo(
  ({ deleteFunction, classes, agreementPageLinkProps, targetProfile, userProfiles, onRequest, t }) => (
    <>
      <ActionButton
        targetProfile={targetProfile}
        userProfiles={userProfiles}
        onRequest={onRequest}
        t={t}
        classes={classes}
      />
      <NextLink href={agreementPageLinkProps.href} as={agreementPageLinkProps.as} type="text">
        <Button color="default" startIcon={<EditFbIcon />} className={classes.editButton}>
          {t(`${TP}.m_EDIT`)}
        </Button>
      </NextLink>
      <IconButton onClick={deleteFunction}>
        <DeleteFbIcon />
      </IconButton>
    </>
  ),
);

const ActionButton = React.memo(({ classes, targetProfile, userProfiles, onRequest, t }) =>
  userProfiles.findIndex(up => up.id === targetProfile?.id) > -1 ? (
    <NextLink href={getRouteWithSlug(route.ARTISTS, targetProfile)}>{t(`${TP}.MANAGE_PROFILE`)}</NextLink>
  ) : (
    <TextButton className={classes.requestLink} onClick={() => onRequest(targetProfile)}>
      {t(`${TP}.ACCESS_REQUEST_SEND`)}
    </TextButton>
  ),
);

export const ArtistsRow = ({
  data,
  showAdditionalAgents,
  editMode,
  t,
  deleteArtistClick,
  agencyUrl,
  userProfiles,
  onRequest,
}) => {
  const classes = useFBTableStyles();
  const commonClasses = useStyles();

  return (
    <TableRow key={get(data, 'id')}>
      <TableCell className={classnames(classes.cell, classes.artistName)}>
        <div className={classes.flexInlineCell}>
          <ArtistInfo t={t} data={data} classes={commonClasses} />
        </div>
      </TableCell>
      <TableCell className={classnames(classes.cell, commonClasses.innerCell)}>
        <AgreementsData agreements={get(data, 'agreements', [])} t={t} commonClasses={commonClasses} />
      </TableCell>
      {showAdditionalAgents && (
        <TableCell className={classnames(classes.cell, commonClasses.innerCell)} style={{ width: '20%' }}>
          {get(data, 'artist.profile.agencies', [])?.map(agency => (
            <NextLink
              className={commonClasses.agencyLink}
              key={agency?.id}
              {...getDynamicRoutePropsForNewTab(route.MANAGERS, agency)}
            >
              {agency?.name}
            </NextLink>
          ))}
        </TableCell>
      )}
      {editMode && (
        <TableCell style={{ width: '10%' }}>
          <div className={classes.flexInlineCell} style={{ justifyContent: 'flex-end' }}>
            <EditButtons
              targetProfile={data?.artist?.profile}
              userProfiles={userProfiles}
              onRequest={onRequest}
              deleteFunction={() => deleteArtistClick(data.id)}
              classes={commonClasses}
              agreementPageLinkProps={addAgreementPageUrl(agencyUrl, data)}
              t={t}
            />
          </div>
        </TableCell>
      )}
    </TableRow>
  );
};

ArtistsRow.propTypes = {
  data: PropTypes.object,
  showAdditionalAgents: PropTypes.bool,
  editMode: PropTypes.bool,
  agencyUrl: PropTypes.string,
};

export const ArtistsCard = ({
  data,
  showAdditionalAgents,
  editMode,
  t,
  deleteArtistClick,
  agencyUrl,
  userProfiles,
}) => {
  const classes = useTableCardStyles();
  const commonClasses = useStyles();

  return (
    <FbTableCard key={get(data, 'id')}>
      <div className={classes.mainInfoElements}>
        <div className={commonClasses.artistNameBlock}>
          <ArtistInfo t={t} data={data} classes={commonClasses} />
          {editMode && (
            <div className={commonClasses.editButtonsBlock}>
              <EditButtons
                userProfiles={userProfiles}
                deleteFunction={() => deleteArtistClick(data.id)}
                classes={commonClasses}
                agreementPageLinkProps={addAgreementPageUrl(agencyUrl, data)}
                t={t}
              />
            </div>
          )}
        </div>
      </div>

      <div className={classes.infoElement}>
        <span className={classes.infoTitle}>{t(`${TP}.m_REPRESENTATIONDETAIL`)}</span>
        <AgreementsData agreements={get(data, 'agreements', [])} t={t} commonClasses={commonClasses} />
      </div>

      {showAdditionalAgents && (
        <div className={classes.infoElement}>
          <span className={classes.infoTitle}>{t(`${TP}.m_OTHERMANAGERSOFTHISARTIST`)}</span>
          {get(data, 'artist.profile.agencies', [])?.map(agency => (
            <NextLink
              className={commonClasses.agencyLink}
              key={agency?.id}
              {...getDynamicRoutePropsForNewTab(route.MANAGERS, agency)}
            >
              {agency?.name}
            </NextLink>
          ))}
        </div>
      )}
    </FbTableCard>
  );
};

ArtistsCard.propTypes = {
  data: PropTypes.object,
  showAdditionalAgents: PropTypes.bool,
  editMode: PropTypes.bool,
  agencyUrl: PropTypes.string,
};
